import { createTheme } from '@mui/material/styles';

// ── Design Tokens ─────────────────────────────────────────────────────────────
export const DS = {
  bg:           '#111320',   // page background
  bg2:          '#171929',   // sidebar, card surfaces
  bg3:          '#1e2133',   // hover states
  bg4:          '#252840',   // deeper hover
  border:       'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.12)',
  text:         '#edeef5',
  textMuted:    '#898ea8',
  textFaint:    '#555870',
  accent:       '#6b5ef8',
  accentDim:    'rgba(107,94,248,0.15)',
  amber:        '#d69d4a',
  amberDim:     'rgba(214,157,74,0.12)',
  amberBorder:  'rgba(214,157,74,0.2)',
} as const;

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: DS.bg,
      paper:   DS.bg2,
    },
    primary: {
      main:         DS.accent,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         DS.textMuted,
      contrastText: '#FFFFFF',
    },
    text: {
      primary:   DS.text,
      secondary: DS.textMuted,
      disabled:  DS.textFaint,
    },
    divider: DS.border,
    error:   { main: '#EF4444' },
    warning: { main: DS.amber },
    success: { main: '#10B981' },
    info:    { main: '#3B82F6' },
  },
  typography: {
    fontFamily: '"DM Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontSize: '2rem',     fontWeight: 600, color: DS.text,     letterSpacing: '-0.05em' },
    h2: { fontSize: '1.5rem',   fontWeight: 600, color: DS.text,     letterSpacing: '-0.03em' },
    h3: { fontSize: '1.25rem',  fontWeight: 600, color: DS.text },
    h4: { fontSize: '1.125rem', fontWeight: 600, color: DS.text },
    h5: { fontSize: '1rem',     fontWeight: 600, color: DS.text },
    h6: { fontSize: '0.875rem', fontWeight: 600, color: DS.text },
    body1:    { fontSize: '0.875rem',   fontWeight: 400, color: DS.text,      lineHeight: 1.6 },
    body2:    { fontSize: '0.8125rem',  fontWeight: 400, color: DS.textMuted,  lineHeight: 1.5 },
    caption:  { fontSize: '0.75rem',    fontWeight: 400, color: DS.textMuted },
    overline: { fontSize: '0.6875rem',  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: DS.textFaint },
    subtitle1:{ fontSize: '0.875rem',   fontWeight: 500, color: DS.text },
    subtitle2:{ fontSize: '0.8125rem',  fontWeight: 500, color: DS.textMuted },
    button:   { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 500, fontSize: '0.875rem', textTransform: 'none', letterSpacing: 0 },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.3)',
    '0 1px 3px rgba(0,0,0,0.35)',
    '0 2px 6px rgba(0,0,0,0.35)',
    '0 4px 12px rgba(0,0,0,0.35)',
    '0 8px 24px rgba(0,0,0,0.35)',
    '0 12px 32px rgba(0,0,0,0.4)',
    '0 16px 40px rgba(0,0,0,0.4)',
    '0 20px 48px rgba(0,0,0,0.42)',
    '0 24px 56px rgba(0,0,0,0.42)',
    '0 28px 64px rgba(0,0,0,0.44)',
    '0 32px 72px rgba(0,0,0,0.44)',
    '0 36px 80px rgba(0,0,0,0.46)',
    '0 40px 88px rgba(0,0,0,0.46)',
    '0 44px 96px rgba(0,0,0,0.48)',
    '0 48px 104px rgba(0,0,0,0.48)',
    '0 52px 112px rgba(0,0,0,0.50)',
    '0 56px 120px rgba(0,0,0,0.50)',
    '0 60px 128px rgba(0,0,0,0.52)',
    '0 64px 136px rgba(0,0,0,0.52)',
    '0 68px 144px rgba(0,0,0,0.54)',
    '0 72px 152px rgba(0,0,0,0.54)',
    '0 76px 160px rgba(0,0,0,0.56)',
    '0 80px 168px rgba(0,0,0,0.56)',
    '0 84px 176px rgba(0,0,0,0.58)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        body: {
          backgroundColor: DS.bg,
          fontFamily: '"DM Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '::-webkit-scrollbar': { width: '6px', height: '6px' },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': { background: DS.textFaint, borderRadius: '3px' },
        '::-webkit-scrollbar-thumb:hover': { background: DS.textMuted },
        // Override browser autofill blue background
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus': {
          WebkitBoxShadow: `0 0 0 1000px ${DS.bg3} inset !important`,
          WebkitTextFillColor: `${DS.text} !important`,
          caretColor: DS.text,
          borderRadius: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.875rem',
          textTransform: 'none',
          letterSpacing: 0,
          padding: '7px 16px',
          transition: 'all 0.15s ease',
        },
        contained: {
          backgroundColor: DS.accent,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#5a4ee0' },
        },
        outlined: {
          borderColor: DS.border,
          color: DS.text,
          '&:hover': { backgroundColor: DS.bg3, borderColor: DS.borderStrong },
        },
        text: {
          color: DS.textMuted,
          '&:hover': { backgroundColor: DS.bg3, color: DS.text },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: DS.bg2,
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: DS.bg2,
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: DS.bg3,
            fontSize: '0.875rem',
            color: DS.text,
            '& fieldset': { borderColor: DS.border },
            '&:hover fieldset': { borderColor: DS.borderStrong },
            '&.Mui-focused fieldset': { borderColor: DS.accent, borderWidth: 1 },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: DS.textMuted,
            '&.Mui-focused': { color: DS.accent },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          backgroundColor: DS.bg3,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { borderCollapse: 'collapse' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: DS.bg2,
            color: DS.textFaint,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${DS.border}`,
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': { backgroundColor: DS.bg3 },
            '&:last-child .MuiTableCell-body': { borderBottom: 'none' },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${DS.border}`,
          fontSize: '0.875rem',
          padding: '12px 16px',
          color: DS.text,
        },
        body: { color: DS.text },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { border: `1px solid ${DS.border}`, borderRadius: 8, overflow: 'hidden' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 22,
        },
        filled: {
          backgroundColor: DS.bg3,
          color: DS.textMuted,
          '&:hover': { backgroundColor: DS.bg4 },
        },
        outlined: {
          borderColor: DS.border,
          color: DS.textMuted,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: DS.border },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: DS.bg4,
          color: DS.text,
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '5px 10px',
          border: `1px solid ${DS.border}`,
        },
        arrow: { color: DS.bg4 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem' },
        standardError:   { backgroundColor: 'rgba(239,68,68,0.1)',  color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)' },
        standardSuccess: { backgroundColor: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)' },
        standardWarning: { backgroundColor: DS.amberDim,            color: DS.amber,  border: `1px solid ${DS.amberBorder}` },
        standardInfo:    { backgroundColor: 'rgba(59,130,246,0.1)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.2)' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: DS.bg3, borderRadius: 9999, height: 4 },
        bar:  { backgroundColor: DS.accent, borderRadius: 9999 },
      },
    },
    MuiCircularProgress: {
      defaultProps: { size: 20, thickness: 4 },
      styleOverrides: {
        root: { color: DS.accent },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: DS.bg3, borderRadius: 4 },
        wave: {
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${DS.bg4}, transparent)`,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: DS.textMuted,
          '&:hover': { backgroundColor: DS.bg3, color: DS.text },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: DS.textMuted,
          minHeight: 40,
          '&.Mui-selected': { color: DS.text, fontWeight: 600 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: DS.accent, height: 2 },
        root: { minHeight: 40 },
      },
    },
    MuiAccordion: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: DS.bg2,
          border: `1px solid ${DS.border}`,
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { minHeight: 48, '&.Mui-expanded': { minHeight: 48 } },
        content: { margin: '12px 0', '&.Mui-expanded': { margin: '12px 0' } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: DS.bg2, border: `1px solid ${DS.border}`, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 600, color: DS.text, padding: '20px 24px 12px' },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '12px 24px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 8 },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 10px',
          '&.Mui-selected': {
            backgroundColor: DS.accentDim,
            color: DS.accent,
            '& .MuiListItemText-primary': { color: DS.accent, fontWeight: 600 },
            '& .MuiListItemIcon-root': { color: DS.accent },
            '&:hover': { backgroundColor: DS.accentDim },
          },
          '&:hover': { backgroundColor: DS.bg3 },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 6,
          margin: '1px 4px',
          '&:hover': { backgroundColor: DS.bg3 },
          '&.Mui-selected': { backgroundColor: DS.bg3, fontWeight: 500 },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: DS.bg2,
          border: 'none',
          borderRight: `1px solid ${DS.border}`,
        },
      },
    },
  },
});

// Chart color palette
export const CHART_COLORS = [
  '#6b5ef8', // accent purple
  '#d69d4a', // amber
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // yellow
  '#EC4899', // pink
  '#8B5CF6', // violet
  '#14B8A6', // teal
];

export default theme;
